"use client";

import Image from "next/image";
import { Sparkles, Flame, Zap } from "lucide-react";
import ProductCard from "./ProductCard";

interface ProductSectionProps {
  title: string;
  products: any[];
  cart: any;
  stockAvailability: any;
  feasibility: any;
  effectiveMaxQty: any;
  onAdd: (key: string, item: any) => void;
  onRemove: (key: string) => void;
  onUpdateQty: (key: string, qty: number) => void;
  backgroundColor?: string;
  textColor?: string;
  iconSrc?: string;
  accentColor?: string;
  onNoPlayer?: () => void;
  isSpecialOffer?: boolean;
}

const ProductSection = ({
  title,
  products,
  cart,
  stockAvailability,
  feasibility,
  effectiveMaxQty,
  onAdd,
  onRemove,
  onUpdateQty,
  backgroundColor = "#EFF6FF",
  textColor = "#1E40AF",
  iconSrc = "/assets/box.svg",
  accentColor,
  onNoPlayer,
  isSpecialOffer = false,
}: ProductSectionProps) => {
  const anyOfferInCart = isSpecialOffer
    ? products.some((p) => (cart[p.id]?.qty || 0) > 0)
    : false;

  const handleAdd = (itemKey: string, item: any) => {
    if (isSpecialOffer) {
      // Auto-swap: remove any other selected offer product from cart
      products.forEach((p) => {
        if (p.id !== itemKey && cart[p.id]) {
          onRemove(p.id);
        }
      });
    }
    onAdd(itemKey, item);
  };

  return (
    <section className="py-12">
      <h2
        className="flex max-w-[calc(100%-16px)] w-full mx-auto items-center justify-center gap-1.5 md:gap-2 text-center text-xs md:text-sm uppercase mb-6 py-2 px-3 md:px-6 font-mono font-bold antialiased"
        style={{
          color: isSpecialOffer ? "#854D0E" : textColor,
          backgroundColor: isSpecialOffer ? "#FEF08A" : backgroundColor,
        }}
      >
        <Image
          src={isSpecialOffer ? "/assets/box-yellow.svg" : iconSrc}
          alt="box"
          width={18}
          height={18}
          className="shrink-0 md:w-[23px] md:h-[23px] object-cover"
        />
        <span className="truncate">{title}</span>
        <Image
          src={isSpecialOffer ? "/assets/box-yellow.svg" : iconSrc}
          alt="box"
          width={18}
          height={18}
          className="shrink-0 md:w-[23px] md:h-[23px]"
        />
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 justify-items-center">
        {products.map((product) => {
          const key = product.id;
          const stockInfo = stockAvailability[key];
          const isOutOfStock = !!(stockInfo && !stockInfo.isAvailable);
          const isLevelUpPass =
            key.startsWith("lvl_") || key.startsWith("id_lvl_");
          const isOfferItem =
            isSpecialOffer ||
            product.isSpecialOffer ||
            product.category === "offers" ||
            key.startsWith("offer_");
          const baseMaxQty = effectiveMaxQty[key] ?? stockInfo?.maxQty ?? 99;
          const maxQty =
            isLevelUpPass || isOfferItem ? Math.min(1, baseMaxQty) : baseMaxQty;
          const qty = cart[key]?.qty || 0;
          const prodFeasibility = feasibility[key];

          const isThisOfferInCart = qty > 0;
          const offerDisabledReason =
            product.disabledReason ||
            (isSpecialOffer && anyOfferInCart && !isThisOfferInCart
              ? "1 Offer Limit"
              : undefined);

          const cardElement = (
            <ProductCard
              product={{
                ...product,
                isSpecialOffer: isSpecialOffer || product.isSpecialOffer,
              }}
              name={product.name}
              price={product.price}
              image={product.image}
              qty={qty}
              maxQty={maxQty}
              isOutOfStock={isOutOfStock}
              feasibility={prodFeasibility}
              onAdd={handleAdd}
              onRemove={onRemove}
              onUpdateQty={onUpdateQty}
              accentColor={accentColor || textColor}
              onNoPlayer={onNoPlayer}
              disabledReason={offerDisabledReason}
              isPurchased={product.isPurchased}
            />
          );

          return (
            <div key={key}>
              {isSpecialOffer ? (
                <div className="running-border-wrapper">
                  <div className="running-border-inner">{cardElement}</div>
                </div>
              ) : (
                cardElement
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ProductSection;
